import { parseAsStringLiteral, useQueryState } from "nuqs";
import React, { useState } from "react";
import { toast } from "sonner";
import { useUpsertDocumentTemplate } from "../../../api/mutations/documents.mutations";
import {
	useGetDocumentMappings,
	useGetDocumentTemplates,
} from "../../../api/queries/documents.queries";
import {
	useGetEmailTemplateMappings,
	useGetEmailTemplates,
} from "../../../api/queries/email-templates.queries";
import PageLayoutWithTabs, {
	Tab,
} from "../../../component/layout/PageLayoutWithTabs";
import CreateTemplateDialog from "./components/CreateTemplateDialog";
import UploadTemplateDialog from "./components/UploadTemplateDialog";
import EmailTemplatesTabContent from "./components/tabs/EmailTemplatesTabContent";
import PlaceholdersTabContent from "./components/tabs/PlaceholdersTabContent";
import TemplatesTabContent from "./components/tabs/TemplatesTabContent";

const tabValues = ["placeholders", "templates", "email-templates"] as const;
type TabValue = (typeof tabValues)[number];

const DocumentTemplatesPage: React.FC = () => {
	const [activeTab, setActiveTab] = useQueryState<TabValue>(
		"tab",
		parseAsStringLiteral<TabValue>(tabValues).withDefault("placeholders"),
	);

	const {
		data: mappings,
		isLoading: isLoadingMappings,
		error: errorMappings,
	} = useGetDocumentMappings();

	const {
		data: templates,
		isLoading: isLoadingTemplates,
		error: errorTemplates,
	} = useGetDocumentTemplates();

	const {
		data: emailTemplates,
		isLoading: isLoadingEmailTemplates,
		error: errorEmailTemplates,
	} = useGetEmailTemplates();

	const {
		data: emailMappings,
		isLoading: isLoadingEmailMappings,
		error: errorEmailMappings,
	} = useGetEmailTemplateMappings();

	const [isUploadOpen, setIsUploadOpen] = useState(false);
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
		null,
	);
	const [isCreateOpen, setIsCreateOpen] = useState(false);

	const handleCopy = (model: string, field: string) => {
		const placeholder = `${model}.${field}`;
		navigator.clipboard.writeText(`{{${placeholder}}}`);
		toast.success(`Copié : {{${placeholder}}}`);
	};

	const openUpload = (id: string) => {
		setSelectedTemplateId(id);
		setIsUploadOpen(true);
	};

	const tabs: Tab<TabValue>[] = [
		{ value: "placeholders", label: "Champs dynamiques" },
		{ value: "templates", label: "Modèles de documents" },
		{ value: "email-templates", label: "Templates d'emails" },
	];

	const placeholdersContent = (
		<PlaceholdersTabContent
			isLoading={isLoadingMappings}
			error={errorMappings}
			mappings={mappings}
			handleCopy={handleCopy}
		/>
	);

	const templatesContent = (
		<TemplatesTabContent
			isLoading={isLoadingTemplates}
			error={errorTemplates}
			templates={templates}
			openUpload={openUpload}
			onCreateTemplate={() => setIsCreateOpen(true)}
		/>
	);

	const emailTemplatesContent = (
		<EmailTemplatesTabContent
			isLoading={isLoadingEmailTemplates}
			error={errorEmailTemplates}
			templates={emailTemplates}
			mappings={emailMappings}
			isLoadingMappings={isLoadingEmailMappings}
			errorMappings={errorEmailMappings}
		/>
	);

	const upsertTemplate = useUpsertDocumentTemplate();

	return (
		<>
			<PageLayoutWithTabs
				title="Gestion des templates"
				activeTab={activeTab}
				onTabChange={(value) => setActiveTab(value)}
				tabs={tabs}
				tabContent={{
					placeholders: placeholdersContent,
					templates: templatesContent,
					"email-templates": emailTemplatesContent,
				}}
			/>
			<UploadTemplateDialog
				open={isUploadOpen}
				onOpenChange={setIsUploadOpen}
				templateId={selectedTemplateId}
				templates={templates}
				onSuccess={() => {
					setIsUploadOpen(false);
					setSelectedTemplateId(null);
				}}
			/>
			<CreateTemplateDialog
				open={isCreateOpen}
				onOpenChange={setIsCreateOpen}
				upsertTemplate={upsertTemplate}
				onSuccess={() => setIsCreateOpen(false)}
			/>
		</>
	);
};

export default DocumentTemplatesPage;
